import { memo, useState } from "react";
import { TextField } from "@mui/material";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";

const DisplayFromFHIRExpression = ({ expression }) => {
  if (expression) {
      if (Array.isArray(expression) || typeof expression !== "object") {
          return <>[ERROR: Expression data not interpretable.]</>;
      } else {
          let description = expression.description;
          let name = expression.name;
          let language = expression.language;
          let expressionString = expression.expression;
          let referenceUri = expression.reference;
          if (language === "text/javascript" && expressionString) {
              return <div style={{ marginLeft: "24px", wordBreak: "break-all" }}>
                  {(name && description) && <><b>{name}:</b> {description}<br /></>}
                  {(name && !description) && <><b>{name}</b>:<br /></>}
                  {(!name && description) && <>{description}<br /></>}
                  {referenceUri && <>Available at: {referenceUri}<br /></>}
                  <p><b>JavaScript:</b></p>
                  <div style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }} >{expressionString}</div>
              </div>
          }
          return <div style={{ marginLeft: "24px", wordBreak: "break-all" }}>
              {(name && description) && <><b>{name}:</b> {description}<br /></>}
              {(name && !description) && <><b>{name}:</b><br /></>}
              {(!name && description) && <>{description}<br /></>}
              {(language && expressionString) && <><b>{language}</b>: {expressionString}</>}
              {(!language && expressionString) && expressionString}
              {(language && !expressionString) && <><b>Language</b>: {language}</>}
              {referenceUri && <><br />Available at: {referenceUri}</>}
          </div>
      }
  } else {
      return <></>
  }
}

const displayStringFromFHIRExpression = (expression) => {
  if (expression === undefined || expression === null || expression === '') {
      return ""
  }
  if (typeof expression === 'object') {
      if (expression.description || expression.name || expression.language || expression.expression || expression.reference) {
          let description = expression.description;
          let name = expression.name;
          let language = expression.language;
          let expressionString = expression.expression;
          let referenceUri = expression.reference;
          let expressionDisplay = "";
          if (name) {
              expressionDisplay = name + ": ";
          }
          if (description) {
              expressionDisplay += description + "; ";
          }
          if (language && expressionString) {
              expressionDisplay += "defined in [" + language + "] as: " + expressionString;
          } else if (language && !expressionString) {
              expressionDisplay += "defined in [" + language + "]";
          } else if (!language && expressionString) {
              expressionDisplay += "defined as: " + expressionString;
          }
          if (referenceUri) {
              expressionDisplay += ", defined at: " + referenceUri;
          }
          return expressionDisplay;
      }
  }
  if (typeof expression === 'string') {
      return `${expression} [ERROR: Expression data not in expected Expression datatype.]`;
  }
  return "[ERROR: Expression data not interpretable.]";
}

const ExpressionEntry = memo(({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
  if (!startingValue) {
    startingValue = "";
  }
  const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

  if (startCollapsedState) {
    return <>
      <div>
        <b>{fieldLabel}: </b>
        {startingValue && <DisplayFromFHIRExpression expression={startingValue} />}
        &nbsp;&nbsp;
        <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
      </div>
    </>
  } else {
    return <div>
      <p style={{ marginBottom: "2px" }}><b>{fieldLabel}: </b></p>
      <div style={{ marginLeft: "24px" }}>
        <span><TextField style={{ width: "100%" }} className="inputField" type='text' label={'Name (short label)'}
          size="small" variant='outlined' value={startingValue.name || ''}
          onChange={(e) => {
            let newExpression = { description: "", name: "", language: "", expression: "", reference: "" };
            newExpression.name = e.target.value;
            if (startingValue.description) { newExpression.description = startingValue.description; } else { delete newExpression.description; }
            if (startingValue.language) { newExpression.language = startingValue.language; } else { delete newExpression.language; }
            if (startingValue.expression) { newExpression.expression = startingValue.expression; } else { delete newExpression.expression; }
            if (startingValue.reference) { newExpression.reference = startingValue.reference; } else { delete newExpression.reference; }
            if (e.target.value || startingValue.description || startingValue.language || startingValue.expression || startingValue.reference) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newExpression }; });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} }; });
            }
          }} /></span>
        <span><TextField style={{ width: "100%" }} className="inputField" type='text' label={'Description (natural language)'}
          size="small" variant='outlined' value={startingValue.description || ''} multiline
          onChange={(e) => {
            let newExpression = { description: "", name: "", language: "", expression: "", reference: "" };
            newExpression.description = e.target.value;
            if (startingValue.name) { newExpression.name = startingValue.name; } else { delete newExpression.name; }
            if (startingValue.language) { newExpression.language = startingValue.language; } else { delete newExpression.language; }
            if (startingValue.expression) { newExpression.expression = startingValue.expression; } else { delete newExpression.expression; }
            if (startingValue.reference) { newExpression.reference = startingValue.reference; } else { delete newExpression.reference; }
            if (e.target.value || startingValue.name || startingValue.language || startingValue.expression || startingValue.reference) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newExpression }; });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} }; });
            }
          }} /></span>
        <span><TextField style={{ width: "100%" }} className="inputField" type='text' label={'Language (e.g. text/cql, text/plain)'}
          size="small" variant='outlined' value={startingValue.language || ''}
          onChange={(e) => {
            let newExpression = { description: "", name: "", language: "", expression: "", reference: "" };
            newExpression.language = e.target.value;
            if (startingValue.name) { newExpression.name = startingValue.name; } else { delete newExpression.name; }
            if (startingValue.description) { newExpression.description = startingValue.description; } else { delete newExpression.description; }
            if (startingValue.expression) { newExpression.expression = startingValue.expression; } else { delete newExpression.expression; }
            if (startingValue.reference) { newExpression.reference = startingValue.reference; } else { delete newExpression.reference; }
            if (e.target.value || startingValue.name || startingValue.description || startingValue.expression || startingValue.reference) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newExpression }; });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} }; });
            }
          }} /></span>
        <span><TextField style={{ width: "100%" }} className="inputField" type='text' label={'Expression (in the specified language)'}
          size="small" variant='outlined' value={startingValue.expression || ''} multiline
          onChange={(e) => {
            let newExpression = { description: "", name: "", language: "", expression: "", reference: "" };
            newExpression.expression = e.target.value;
            if (startingValue.name) { newExpression.name = startingValue.name; } else { delete newExpression.name; }
            if (startingValue.language) { newExpression.language = startingValue.language; } else { delete newExpression.language; }
            if (startingValue.description) { newExpression.description = startingValue.description; } else { delete newExpression.description; }
            if (startingValue.reference) { newExpression.reference = startingValue.reference; } else { delete newExpression.reference; }
            if (e.target.value || startingValue.name || startingValue.language || startingValue.description || startingValue.reference) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newExpression }; });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} }; });
            }
          }} /></span>
        <span><TextField style={{ width: "100%" }} className="inputField" type='text' label={'Reference (URL)'}
          size="small" variant='outlined' value={startingValue.reference || ''}
          onChange={(e) => {
            let newExpression = { description: "", name: "", language: "", expression: "", reference: "" };
            newExpression.reference = e.target.value;
            if (startingValue.name) { newExpression.name = startingValue.name; } else { delete newExpression.name; }
            if (startingValue.description) { newExpression.description = startingValue.description; } else { delete newExpression.description; }
            if (startingValue.expression) { newExpression.expression = startingValue.expression; } else { delete newExpression.expression; }
            if (startingValue.language) { newExpression.language = startingValue.language; } else { delete newExpression.language; }
            if (e.target.value || startingValue.name || startingValue.description || startingValue.expression || startingValue.language) {
              setResourceState(prevState => { return { ...prevState, [elementName]: newExpression }; });
            } else {
              setResourceState(prevState => { return { ...prevState, [elementName]: {} }; });
            }
          }} /></span>
      </div>
    </div>
  }
});

export default ExpressionEntry;
export { DisplayFromFHIRExpression, displayStringFromFHIRExpression };